import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import { Calculator, LockKey, LockKeyOpen, ArrowsClockwise, Printer, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { openPrintStatement } from "@/lib/printStatement";

const today = () => new Date().toISOString().slice(0, 10);
const Kpi = ({ label, value, color, foot, testid }) => (
  <div className="kpi" data-testid={testid}>
    <div className="eyebrow">{label}</div>
    <div className="val" style={color ? { color } : undefined}>{value}</div>
    {foot ? <div className="text-[11px] font-mono mt-1 text-muted-foreground">{foot}</div> : null}
  </div>
);

// Reusable movements table for Cash / UPI / Bank lanes on the daily close.
const MovementsTable = ({ title, date, rows, testid, showMode = false }) => {
  const inTotal = rows.filter((r) => r.direction === "in").reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const outTotal = rows.filter((r) => r.direction === "out").reduce((a, r) => a + (Number(r.amount) || 0), 0);
  return (
    <div className="card-flat overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow">{title} — {date}</div>
          <div className="font-heading text-lg font-semibold">{rows.length} entry(ies)</div>
        </div>
        <div className="text-[11.5px] font-mono flex gap-4">
          <span style={{ color: "hsl(var(--success))" }}>In: {inr(inTotal)}</span>
          <span style={{ color: "hsl(var(--destructive))" }}>Out: {inr(outTotal)}</span>
        </div>
      </div>
      <table className="data-table" data-testid={testid}>
        <thead>
          <tr>
            <th>Voucher #</th><th>Type</th><th>Description</th>
            {showMode && <th>Mode</th>}
            <th>Reference</th><th className="num">In</th><th className="num">Out</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={showMode ? 7 : 6} className="text-center text-muted-foreground py-6">No {title.toLowerCase()} today.</td></tr>
          ) : rows.map((r) => (
            <tr key={`${r.kind}-${r.id}`}>
              <td className="font-mono">{r.number || "—"}</td>
              <td className="text-[11.5px] uppercase tracking-wider">{r.voucher_type}</td>
              <td>{r.party_name}</td>
              {showMode && <td className="text-[11.5px] uppercase tracking-wider text-muted-foreground">{r.mode || "—"}</td>}
              <td className="font-mono text-[12px]">{r.reference || "—"}</td>
              <td className="num font-mono" style={{ color: "hsl(var(--success))" }}>{r.direction === "in" ? inr(r.amount) : ""}</td>
              <td className="num font-mono" style={{ color: "hsl(var(--destructive))" }}>{r.direction === "out" ? inr(r.amount) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function DailyCashClose() {
  const { activeCompanyId, activeCompany, activeStoreId, activeStore } = useAuth();
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [physical, setPhysical] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (!activeCompanyId) return;
    api.get(`/cashbook/daily-close?date=${date}`)
      .then((r) => {
        setData(r.data);
        setPhysical(r.data.close?.physical_count != null ? String(r.data.close.physical_count) : String(r.data.closing.cash));
        setNotes(r.data.close?.notes || "");
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, [activeCompanyId, activeStoreId, date, refreshKey]);

  const closeDay = async () => {
    setSaving(true);
    try {
      const { data: closeResult } = await api.post("/cashbook/daily-close", {
        date, physical_count: Number(physical) || 0, notes,
      });
      // Surface email delivery status separately so the user knows whether
      // the closing report actually reached the configured recipients.
      const es = closeResult?.email_status;
      const pw = closeResult?.pending_works_email_status;
      const pwMsg = pw?.ok && pw.sent_count > 0
        ? ` · Pending Works statement also sent`
        : (pw && pw.failed_count > 0 ? ` · Pending Works email failed` : "");
      if (es?.ok && es.sent_count > 0) {
        toast.success(
          `Day closed · Report emailed to ${es.sent_count} recipient${es.sent_count > 1 ? "s" : ""}${pwMsg}`,
          { duration: 6000 }
        );
      } else if (es && es.failed_count > 0) {
        toast.error(
          `Day closed BUT email failed for ${es.failed_count} recipient(s): ${(es.errors || []).join(" · ") || "see server logs"}`,
          { duration: 10000 }
        );
      } else if (es && (es.errors || []).length) {
        toast.error(
          `Day closed BUT email not sent — ${es.errors.join(" · ")}`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Day closed${pwMsg}`);
      }
      // Refresh, then auto-share the daily closing report on WhatsApp
      try {
        const r = await api.get(`/cashbook/daily-close?date=${date}`);
        setData(r.data);
        setPhysical(r.data.close?.physical_count != null ? String(r.data.close.physical_count) : String(r.data.closing.cash));
        setNotes(r.data.close?.notes || "");
        sendWhatsApp(r.data);
      } catch { setRefreshKey((k) => k + 1); }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const reopen = async () => {
    if (!window.confirm("Reopen this day? The saved physical count will be discarded.")) return;
    setSaving(true);
    try {
      await api.delete(`/cashbook/daily-close/${date}`);
      toast.success("Day reopened");
      setRefreshKey((k) => k + 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="page-shell text-sm text-muted-foreground">Loading day summary…</div>;

  const variance = (Number(physical) || 0) - data.closing.cash;
  const t = data.totals;

  const printStatement = () => {
    const cashIn = data.cash_lines.filter((r) => r.direction === "in");
    const cashOut = data.cash_lines.filter((r) => r.direction === "out");
    const cols = [
      { key: "number", label: "Voucher #", mono: true },
      { key: "voucher_type", label: "Type" },
      { key: "party_name", label: "Description" },
      { key: "reference", label: "Reference", mono: true },
      { key: "amount", label: "Amount", align: "right", mono: true },
    ];
    const mapRow = (r) => ({
      number: r.number || "—",
      voucher_type: String(r.voucher_type || "").toUpperCase(),
      party_name: r.party_name || "",
      reference: r.reference || "—",
      amount: inr(r.amount),
    });
    const sections = [];
    if (cashIn.length) sections.push({
      heading: "Cash Receipts (Money In)",
      subheading: `${cashIn.length} entry(ies) · Total <b>${inr(t.cash_in)}</b>`,
      columns: cols,
      rows: cashIn.map(mapRow),
    });
    if (cashOut.length) sections.push({
      heading: "Cash Payments (Money Out)",
      subheading: `${cashOut.length} entry(ies) · Total <b>${inr(t.cash_out)}</b>`,
      columns: cols,
      rows: cashOut.map(mapRow),
    });
    const varianceLabel = Math.abs(variance) < 0.01 ? "Variance (tallies)" : variance > 0 ? "Variance (surplus)" : "Variance (short)";
    const res = openPrintStatement({
      title: `Daily Cash Close · ${date}`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Opening Cash", value: inr(data.opening.cash) },
        { label: "Receipts (Cash)", value: inr(t.cash_in), tone: "pos" },
        { label: "Payments (Cash)", value: inr(t.cash_out), tone: "neg" },
        { label: "System Closing", value: inr(data.closing.cash), tone: data.closing.cash < 0 ? "neg" : "pos" },
        { label: "Physical Count", value: inr(Number(physical) || 0) },
        { label: varianceLabel, value: inr(variance), tone: Math.abs(variance) < 0.01 ? "pos" : variance > 0 ? "" : "neg" },
      ],
      note: notes
        ? `Notes: ${notes}`
        : (data.close?.closed
          ? `Day closed by ${data.close.closed_by} at ${String(data.close.closed_at).slice(0, 16).replace("T", " ")}.`
          : "Day not yet closed — figures reflect current system state."),
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${data.cash_lines.length} movement(s)…`);
  };

  const sendEmail = async () => {
    setEmailSending(true);
    try {
      const overrides = emailTo
        .split(/[\s,;]+/)
        .map((e) => e.trim())
        .filter((e) => e && e.includes("@"));
      const { data: res } = await api.post("/cashbook/daily-close/email", {
        date,
        recipients: overrides.length ? overrides : undefined,
      });
      if (res?.ok && res.sent_count > 0) {
        toast.success(
          `Report emailed to ${res.sent_count} recipient${res.sent_count > 1 ? "s" : ""}`,
          { duration: 6000 }
        );
        setEmailOpen(false);
        setEmailTo("");
      } else if (res?.failed_count > 0) {
        toast.error(
          `Email failed for ${res.failed_count} recipient(s): ${(res.errors || []).join(" · ") || "see server logs"}`,
          { duration: 10000 }
        );
      } else {
        toast.error((res?.errors || []).join(" · ") || "Email not sent");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailSending(false); }
  };

  const sendWhatsApp = (snapshot) => {
    const snap = snapshot || data;
    if (!snap || !snap.closing || !snap.totals) {
      toast.error("Day summary is still loading — please try again in a moment.");
      return;
    }
    const snapTotals = snap.totals;
    const snapVariance = (Number(physical) || 0) - snap.closing.cash;
    const compName = activeCompany?.name || "Company";
    const compWa = String(activeCompany?.whatsapp_number || "").replace(/[^0-9]/g, "");
    const storeName = activeStore?.name ? ` · ${activeStore.name}` : "";
    const varLabel = Math.abs(snapVariance) < 0.01
      ? `${inr(snapVariance)} (tallies)`
      : snapVariance > 0 ? `${inr(snapVariance)} (surplus)` : `${inr(snapVariance)} (short)`;
    // Build per-lane blocks (Cash / UPI / Bank) so the shared message mirrors
    // the on-screen split tables, line by line.
    const modeLabelMap = {
      rtgs_neft: "NEFT/RTGS",
      cheque: "Cheque",
      credit_card: "Credit Card",
      debit_card: "Debit Card",
      transfer: "Transfer",
      other: "Other",
      upi: "UPI",
      cash: "Cash",
    };
    const prettyMode = (m) => modeLabelMap[(m || "").toLowerCase()] || String(m || "").toUpperCase() || "—";
    const laneBlock = (title, rows, { showMode = false } = {}) => {
      if (!rows || !rows.length) return [];
      const inRows = rows.filter((r) => r.direction === "in");
      const outRows = rows.filter((r) => r.direction === "out");
      const inTot = inRows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
      const outTot = outRows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
      const fmt = (r) => {
        const mode = showMode ? ` [${prettyMode(r.mode)}]` : "";
        const label = r.party_name || r.number || "—";
        return `  • ${label}${mode} — ${inr(r.amount)}`;
      };
      const out = ["", `*${title}* (${rows.length} entry${rows.length === 1 ? "" : "s"})`];
      if (inRows.length) {
        out.push(`  _In: ${inr(inTot)}_`, ...inRows.map(fmt));
      }
      if (outRows.length) {
        out.push(`  _Out: ${inr(outTot)}_`, ...outRows.map(fmt));
      }
      return out;
    };

    const bankIn = (snap.bank_lines || []).filter((r) => r.direction === "in").reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const bankOut = (snap.bank_lines || []).filter((r) => r.direction === "out").reduce((a, r) => a + (Number(r.amount) || 0), 0);

    const text = [
      `*${compName}${storeName} — Daily Cash Close*`,
      `Date: ${date}`,
      "",
      `Opening Cash:  ${inr(snap.opening.cash)}`,
      `Receipts (In): ${inr(snapTotals.cash_in)}`,
      `Payments (Out): ${inr(snapTotals.cash_out)}`,
      `Closing Cash: *${inr(snap.closing.cash)}*`,
      `Physical Count: ${inr(Number(physical) || 0)}`,
      `Variance: ${varLabel}`,
      "",
      `Bank In: ${inr(bankIn)} · Bank Out: ${inr(bankOut)}`,
      `Deposited to Bank: ${inr(snapTotals.deposited_to_bank)}`,
      `Withdrawn from Bank: ${inr(snapTotals.withdrawn_from_bank)}`,
      `Bank Closing: ${inr(snap.closing.bank)}`,
      ...laneBlock("Cash Movements", snap.cash_lines || []),
      ...laneBlock("Bank Movements", snap.bank_lines || [], { showMode: true }),
    ];
    if (notes) text.push("", `Notes: ${notes}`);
    if (snap.close?.closed) {
      text.push("", `Closed by ${snap.close.closed_by} at ${String(snap.close.closed_at).slice(0, 16).replace("T", " ")}`);
    } else {
      text.push("", "_Day not yet closed — figures reflect current system state._");
    }
    text.push("", `— Sent from ${compName}${storeName}`);

    const encoded = encodeURIComponent(text.join("\n"));
    const url = compWa ? `https://wa.me/${compWa}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(compWa ? `WhatsApp opened for ${compWa}` : "WhatsApp opened — pick a contact");
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Calculator size={11} /> End-of-day</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Daily Cash Close</h1>
          <p className="text-sm text-muted-foreground mt-1">Opening cash · movements · closing cash · physical count · variance — all in one tap.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <div className="space-y-1.5">
            <Label className="eyebrow">Date</Label>
            <Input type="date" min="1900-01-01" value={date} onChange={(e) => setDate(e.target.value)} className="font-mono h-9 w-40" data-testid="close-date" />
          </div>
          <Button variant="outline" onClick={() => setRefreshKey((k) => k + 1)} className="h-9 mt-6" data-testid="close-refresh">
            <ArrowsClockwise size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" onClick={printStatement} className="h-9 mt-6" data-testid="close-print-statement">
            <Printer size={13} className="mr-1.5" /> Print Statement
          </Button>
          <Button
            variant="outline"
            onClick={sendWhatsApp}
            className="h-9 mt-6 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 hover:text-[#128C7E]"
            data-testid="close-send-whatsapp"
            title="Send full statement (receipts + payments + balances) on WhatsApp">
            <WhatsappLogo weight="fill" size={14} className="mr-1.5" /> WhatsApp Statement
          </Button>
          <Button
            variant="outline"
            onClick={() => setEmailOpen(true)}
            className="h-9 mt-6"
            data-testid="close-send-email"
            title="Email the full daily close report">
            <EnvelopeSimple size={14} className="mr-1.5" /> Email Statement
          </Button>
          <PageExportBar
            title={`Daily Cash Close · ${date}`}
            filename={`daily-cash-close-${date}`}
            columns={[
              { key: "number", label: "Voucher #" },
              { key: "voucher_type", label: "Type" },
              { key: "party_name", label: "Description" },
              { key: "reference", label: "Reference" },
              { key: "direction", label: "Direction" },
              { key: "amount", label: "Amount" },
            ]}
            rows={data.cash_lines}
            testidPrefix="close"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Opening Cash" value={inr(data.opening.cash)} foot="Start of day" testid="close-opening" />
        <Kpi label="Receipts (Cash)" value={inr(t.cash_in)} color="hsl(var(--success))" testid="close-receipts" />
        <Kpi label="Payments (Cash)" value={inr(t.cash_out)} color="hsl(var(--destructive))" testid="close-payments" />
        <Kpi label="Closing Cash" value={inr(data.closing.cash)} color={data.closing.cash < 0 ? "hsl(var(--destructive))" : "hsl(var(--success))"} foot="Computed" testid="close-closing" />
        <Kpi label="Deposited to Bank" value={inr(t.deposited_to_bank)} testid="close-deposited" />
        <Kpi label="Withdrawn from Bank" value={inr(t.withdrawn_from_bank)} testid="close-withdrawn" />
        <Kpi label="Bank Closing" value={inr(data.closing.bank)} color="hsl(var(--primary))" foot="Includes UPI" testid="close-bank" />
      </div>

      <div className="card-flat p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">Reconciliation</div>
            <div className="font-heading text-lg font-semibold">Verify against physical count</div>
          </div>
          {data.close.closed && (
            <div className="text-[11px] font-mono text-muted-foreground">
              Closed by {data.close.closed_by} at {String(data.close.closed_at).slice(0, 16).replace("T", " ")}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="eyebrow">Physical Cash Count</Label>
            <Input type="number" step="0.01" value={physical} onChange={(e) => setPhysical(e.target.value)} className="font-mono text-lg h-11" data-testid="close-physical" disabled={data.close.closed} />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">System Closing</Label>
            <div className="rounded border border-border p-2.5 font-mono text-lg">{inr(data.closing.cash)}</div>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Variance</Label>
            <div className="rounded border border-border p-2.5 font-mono text-lg" style={{ color: Math.abs(variance) < 0.01 ? "hsl(var(--success))" : variance > 0 ? "hsl(var(--warning))" : "hsl(var(--destructive))" }} data-testid="close-variance">
              {inr(variance)} {Math.abs(variance) < 0.01 ? "✓ tallies" : variance > 0 ? "(surplus)" : "(short)"}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any variance reason, cash handed over to owner, etc." disabled={data.close.closed} />
        </div>
        <div className="flex items-center gap-2 no-print">
          {data.close.closed ? (
            <Button variant="outline" onClick={reopen} disabled={saving} data-testid="close-reopen-btn">
              <LockKeyOpen size={13} className="mr-1.5" /> Reopen Day
            </Button>
          ) : (
            <Button onClick={closeDay} disabled={saving} data-testid="close-day-btn">
              <LockKey size={13} className="mr-1.5" /> {saving ? "Saving…" : "Close Day"}
            </Button>
          )}
        </div>
      </div>

      <MovementsTable
        title="Cash Movements"
        date={date}
        rows={data.cash_lines || []}
        testid="close-cash-table"
      />
      {(() => {
        // Split the bank lane into one table per payment mode so NEFT / RTGS,
        // Cheque, Cards, transfers and "other bank" each get their own view.
        const modeLabel = {
          rtgs_neft: "NEFT / RTGS",
          cheque: "Cheque",
          credit_card: "Credit Card",
          debit_card: "Debit Card",
          upi: "UPI",
          other: "Other Bank",
          transfer: "Cash ↔ Bank Transfers",
          "": "Bank (Unspecified)",
        };
        const groups = {};
        (data.bank_lines || []).forEach((r) => {
          const key = (r.mode || "other").toLowerCase();
          (groups[key] = groups[key] || []).push(r);
        });
        // Preferred display order — familiar accounting modes first.
        const order = ["upi", "rtgs_neft", "cheque", "credit_card", "debit_card", "other", "transfer", ""];
        const keys = [
          ...order.filter((k) => groups[k] && groups[k].length),
          ...Object.keys(groups).filter((k) => !order.includes(k)),
        ];
        if (keys.length === 0) {
          return (
            <MovementsTable
              title="Bank Movements"
              date={date}
              rows={[]}
              testid="close-bank-table"
            />
          );
        }
        return keys.map((k) => (
          <MovementsTable
            key={`bank-${k}`}
            title={`${modeLabel[k] || k.toUpperCase()} Movements`}
            date={date}
            rows={groups[k]}
            testid={`close-bank-${k || "unspecified"}-table`}
          />
        ));
      })()}

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="close-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Daily Cash Close · {date}</DialogTitle>
            <DialogDescription>
              Leave blank to send to configured recipients in Integrations → Daily Close Report Recipients.
              Add comma-separated addresses to override for this send only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="eyebrow">Recipients (optional override)</Label>
            <Textarea
              rows={3}
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="owner@example.com, accountant@example.com"
              data-testid="close-email-recipients"
            />
            <p className="text-[11px] text-muted-foreground">
              The email includes today's opening cash, totals, closing cash, bank closing (incl. UPI), and the full Cash / Bank movement breakdowns.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending} data-testid="close-email-cancel">Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="close-email-send">
              <EnvelopeSimple size={13} className="mr-1.5" />
              {emailSending ? "Sending…" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
